import { useEffect, useRef } from 'react';

import { useConfluenceAnalyticsEvents } from '@confluence/analytics/entry-points/useConfluenceAnalyticsEvents.ts';

import type { StaticMacroResultMetadata } from '../components/StaticMacroStoreContext.tsx';

type UseStaticMacroRenderAnalyticsArgs = {
	html: string | undefined;
	errorCode: string | undefined;
	result: StaticMacroResultMetadata | undefined;
	isEditing: boolean;
	isLivePage: boolean;
};

export function useStaticMacroRenderAnalytics({
	html,
	errorCode,
	result,
	isEditing,
	isLivePage,
}: UseStaticMacroRenderAnalyticsArgs): void {
	const { createEvent } = useConfluenceAnalyticsEvents();
	const reportedResultVersionsRef = useRef<Set<number>>(new Set());

	useEffect(() => {
		if (!html || !result || reportedResultVersionsRef.current.has(result.version)) {
			return;
		}

		reportedResultVersionsRef.current.add(result.version);
		createEvent({
			type: 'sendOperationalEvent',
			data: {
				source: 'forgeStaticMacro',
				action: 'rendered',
				actionSubject: 'staticMacro',
				attributes: {
					attempt: result.attempt,
					renderMode: 'static',
					isEditing,
					isLivePage,
				},
			},
		}).fire();
	}, [createEvent, html, isEditing, isLivePage, result]);

	useEffect(() => {
		if (!errorCode || html || !result || reportedResultVersionsRef.current.has(result.version)) {
			return;
		}

		reportedResultVersionsRef.current.add(result.version);
		createEvent({
			type: 'sendOperationalEvent',
			data: {
				source: 'forgeStaticMacro',
				action: 'failed',
				actionSubject: 'staticMacro',
				attributes: {
					attempt: result.attempt,
					renderMode: 'iframe-fallback',
					errorCode,
					isEditing,
					isLivePage,
				},
			},
		}).fire();
	}, [createEvent, errorCode, html, isEditing, isLivePage, result]);
}
